import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowUpIcon, SendHorizonal } from "lucide-react";
import backend from "../../backend/backend";
import Message from "../../i18n/Message";
import label from "../../i18n/label";
import { currentModelDescriptions } from "../../model/model-providers/modelDescriptors";
import { createProject } from "./store/builderActionCreators";
import NewWorkspaceLayout from "../workspace/layout/NewWorkspaceLayout";
import { linkToBuilder, linkToBuilderProject } from "../../routes";

import "./NewBuilderProjectPage.scss";

const NewBuilderProjectPage = () => {
  const [prompt, setPrompt] = useState("");
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [textareaStyles, setTextareaStyles] = useState({ height: "84px" });

  const dispatch = useDispatch();
  const intl = useIntl();
  const nav = useNavigate();
  const { workspaceId } = useParams();

  // Load vendor keys and determine available models
  useEffect(() => {
    const loadVendorKeys = async () => {
      try {
        setIsLoading(true);
        const vendorKeys = await backend.getVendorKeys(workspaceId);

        // Find vendors with configured keys (non-empty content)
        const availableVendors = vendorKeys
          .filter((key) => key.content && key.content.trim() !== "")
          .map((key) => key.vendor);

        // Filter models by available vendors
        const models = currentModelDescriptions.filter((model) =>
          availableVendors.includes(model.vendor)
        );

        setAvailableModels(models);

        // Auto-select first model if available
        if (models.length > 0) {
          setSelectedModelId(models[0].id);
          setError(null);
        } else {
          setError("no_vendor_keys_error");
        }
      } catch (err) {
        console.error("Failed to load vendor keys", err);
        setAvailableModels([]);
        setError("no_vendor_keys_error");
      } finally {
        setIsLoading(false);
      }
    };

    if (workspaceId) {
      loadVendorKeys();
    }
  }, [workspaceId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedModelId) {
      setError("no_vendor_keys_error");
      return;
    }

    if (!prompt.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Generate a project name from the first few words of the prompt
      const projectName =
        prompt.trim().substring(0, 50) + (prompt.length > 50 ? "..." : "");

      const project = await dispatch(
        createProject(intl, workspaceId, {
          name: projectName,
          modelId: selectedModelId,
        })
      );

      if (project && project.chatUid) {
        // Navigate to project chat page with prompt as query param
        const encodedPrompt = encodeURIComponent(prompt);
        nav(
          `${linkToBuilderProject(
            workspaceId,
            project.chatUid
          )}?prompt=${encodedPrompt}`
        );
      }
    } catch (err) {
      console.error("Failed to create project", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    // Submit on Shift+Enter
    if (e.keyCode === 13 && e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleKeyUp = (e) => {
    const elem = e.target;

    // Only adjust height if content is overflowing (scrollHeight > clientHeight)
    if (elem.scrollHeight > elem.clientHeight) {
      if (elem.scrollHeight <= 220) {
        // Grow textarea to fit content
        setTextareaStyles({
          overflow: "hidden",
          height: elem.scrollHeight + 2 + "px",
        });
      } else {
        // Max height reached, show scrollbar
        setTextareaStyles({
          overflow: "auto",
          height: "220px",
        });
      }
    }
  };

  return (
    <NewWorkspaceLayout contentPanelClassName="new_builder_project_page">
      <div className="breadcrumbs">
        <Link to={linkToBuilder(workspaceId)}>
          <Message id="app_builder_projects_title" />
        </Link>
        <span className="separator">/</span>
        <span>
          <Message id="new_builder_project" />
        </span>
      </div>

      <div className="page_content">
        <div className="prompt_textarea_container">
          <form onSubmit={handleSubmit}>
            <div className="textarea_container">
              <textarea
                className="prompt_textarea"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                style={textareaStyles}
                placeholder={label(
                  intl,
                  "enter_prompt",
                  "Describe what you want to build..."
                )}
                disabled={isSubmitting || availableModels.length === 0}
                autoFocus
              />
            </div>
            <div className="actions_container">
              <div className="actions_left">
                <div className="model_panel">
                  {isLoading ? (
                    <div className="loading_models">Loading...</div>
                  ) : (
                    <>
                      <select
                        className="form-control model_select"
                        value={selectedModelId || ""}
                        onChange={(e) => {
                          setSelectedModelId(e.target.value);
                          setError(null);
                        }}
                        disabled={availableModels.length === 0}
                      >
                        {availableModels.length === 0 && (
                          <option value="">
                            {label(
                              intl,
                              "no_models_available",
                              "No models available"
                            )}
                          </option>
                        )}
                        {availableModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name}
                          </option>
                        ))}
                      </select>
                      {error && (
                        <div className="error_message">
                          <Message id={error} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="actions_right">
                <span className="hint">
                  {label(intl, "shift_enter_hint", "Press Shift+Enter to send")}
                </span>
                <button
                  type="submit"
                  className="btn btn-primary send_button"
                  disabled={isSubmitting || !prompt.trim() || !selectedModelId}
                >
                  {isSubmitting ? (
                    <Message id="creating" defaultMessage="Creating..." />
                  ) : (
                    <>
                      <ArrowUpIcon size={20} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </NewWorkspaceLayout>
  );
};

export default NewBuilderProjectPage;
